import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
  IconButton,
} from '@mui/material';
import { projectsAPI, api } from '../../services/api';
import { dailyReportsAPI } from '../../services/dailyReportsAPI';
import { useSnackbar } from 'notistack';
import { Project, AnwesenderEintrag } from '../../types/api';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { API_BASE_URL } from '../../config';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionIcon from '@mui/icons-material/Description';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

interface FormData {
  project_id: number | string;
  name: string;
  projekt: string;
  strasse: string;
  ort: string;
  berichtnummer: string;
  datum: Date;
  ersteller: string;
  // Witterung
  uhrzeit: string;
  bewoelkung: string;
  wetterlage: string;
  wind: string;
  temperatur: string;
  luftfeuchtigkeit: string;
  // Anwesende
  anwesende: AnwesenderEintrag[];
  // Anmerkungen
  anmerkungen: string;
  // Für Backend-Kompatibilität
  weather: string;
  activities: string;
  beauftragte_personen: string[];
  beauftragungsart: string;
  beauftragungsbeschreibung: string;
  unterschrift: string;
  status: string;
}

export const DailyReportFormTagesbericht: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  // Rollen-basierte Berechtigungen
  const isAdmin = user?.role === 'admin';
  const isManagement = user?.role === 'management';
  const isBauleiter = user?.role === 'bauleiter';
  const isForeman = user?.role === 'foreman';
  const isInstaller = user?.role === 'installer';
  
  // Admin, Management & Bauleiter können alles
  const canSeeAll = isAdmin || isManagement || isBauleiter;
  // Vorarbeiter & Monteure sehen nur eingeschränkte Daten
  const canSeeRestricted = isForeman || isInstaller;
  // Alle können Tagesberichte erstellen
  const canCreateReports = isAdmin || isManagement || isBauleiter || isForeman || isInstaller;
  // Nur Admin, Management & Bauleiter können alle bearbeiten
  const canEditAllReports = canSeeAll;
  // Nur Admin, Management & Bauleiter können exportieren
  const canExportReports = canSeeAll;

  // Helper function to extract city from address
  const extractCityFromAddress = (address: string): string => {
    if (!address) return '';
    const lines = address.split('\n');
    const plzCityMatch = lines[lines.length - 1].trim().match(/^\d{5}\s+(.+)$/);
    return plzCityMatch ? plzCityMatch[1] : lines[lines.length - 1].trim();
  };
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<{ label: string; value: number; project: Project }[]>([]);
  const [photos, setPhotos] = useState<File[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<{ id: number; file_path: string }[]>([]);
  const [photoToDelete, setPhotoToDelete] = useState<{ id: number; file_path: string } | null>(null);
  const [documents, setDocuments] = useState<File[]>([]);
  const [existingDocuments, setExistingDocuments] = useState<any[]>([]);
  const [previewPhoto, setPreviewPhoto] = useState<string | null>(null);

  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });

  const [formValues, setFormValues] = useState<FormData>({
    project_id: '',
    name: 'Tagesbericht Rooftop Bedachungen',
    projekt: '',
    strasse: '',
    ort: '',
    berichtnummer: '',
    datum: new Date(),
    ersteller: user?.email || '',
    uhrzeit: '',
    bewoelkung: '',
    wetterlage: '',
    wind: '',
    temperatur: '',
    luftfeuchtigkeit: '',
    anwesende: [],
    anmerkungen: '',
    weather: '',
    activities: '',
    beauftragte_personen: [],
    beauftragungsart: '',
    beauftragungsbeschreibung: '',
    unterschrift: '',
    status: 'entwurf'
  });

  const getNextReportNumber = async () => {
    // Nur ausführen wenn project_id gesetzt ist
    if (!formValues.project_id || formValues.project_id === '') {
      console.log('❌ Keine project_id gesetzt, überspringe Berichtnummer-Generierung');
      return;
    }
    
    console.log('🔍 Generiere Berichtnummer für Projekt:', formValues.project_id);
    
    try {
      const projectId = parseInt(formValues.project_id.toString());
      console.log('📡 API-Call für Projekt-ID:', projectId);
      
      const nextNumber = await dailyReportsAPI.getNextReportNumber(projectId);
      console.log('✅ API-Response:', nextNumber);
      
      setFormValues(prev => ({
        ...prev,
        berichtnummer: nextNumber
      }));
      console.log('✅ Berichtnummer gesetzt auf:', nextNumber);
    } catch (error) {
      console.error('❌ API-Fehler beim Abrufen der Berichtnummer:', error);
      // Fallback: manuelle Berechnung für das spezifische Projekt
      try {
        console.log('🔄 Verwende Fallback-Berechnung...');
        const reportsResponse = await dailyReportsAPI.getReports();
        const projectId = parseInt(formValues.project_id.toString());
        const projectReports = reportsResponse.items.filter(
          report => report.project_id === projectId
        );
        const nextNumber = projectReports.length + 1;
        console.log('📊 Fallback: Projekt hat', projectReports.length, 'Berichte, nächste Nummer:', nextNumber);
        
        setFormValues(prev => ({
          ...prev,
          berichtnummer: nextNumber.toString()
        }));
        console.log('✅ Fallback-Berichtnummer gesetzt auf:', nextNumber);
      } catch (fallbackError) {
        console.error('❌ Fallback-Berechnung fehlgeschlagen:', fallbackError);
      }
    }
  };

  // Automatische Berichtnummer-Generierung bei Projekt-Auswahl
  useEffect(() => {
    if (formValues.project_id && formValues.project_id !== '' && !id) {
      console.log('🔄 Automatische Berichtnummer-Generierung für Projekt:', formValues.project_id);
      getNextReportNumber();
    }
  }, [formValues.project_id, id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const projectsResponse = await projectsAPI.getProjects();
        const projectsOptions = projectsResponse.map((project: Project) => ({
          label: project.name,
          value: project.id,
          project: project
        }));
        setProjects(projectsOptions);
        
        if (id) {
          const loadReport = async () => {
            try {
              setLoading(true);
              const report = await dailyReportsAPI.getReport(Number(id));
              console.log('Loaded report data:', report); // Debug log
              
              // Berechtigung prüfen: Vorarbeiter & Monteure können nur eigene Berichte bearbeiten
              // und nur wenn Status = "entwurf"
              if (canSeeRestricted && !canSeeAll) {
                const isOwnReport = report.created_by === user?.id || 
                                   report.ersteller === user?.email;
                const isDraft = report.status === 'entwurf';
                
                if (!isOwnReport) {
                  enqueueSnackbar('Sie können nur Ihre eigenen Berichte bearbeiten', { variant: 'error' });
                  navigate('/tagesberichte');
                  return;
                }
                
                if (!isDraft) {
                  enqueueSnackbar('Eingereichte Berichte können nicht mehr bearbeitet werden', { variant: 'error' });
                  navigate('/tagesberichte');
                  return;
                }
              }
              
              // Lade Dokumente
              if (report.documents) {
                setExistingDocuments(report.documents);
              }
              
              setFormValues(prev => ({
                ...prev,
                project_id: report.project_id?.toString() ?? prev.project_id,
                datum: report.date ? new Date(report.date) : prev.datum,
                weather: report.weather ?? prev.weather,
                activities: report.activities ?? prev.activities,
                name: report.name ?? prev.name,
                projekt: report.projekt ?? prev.projekt,
                strasse: report.strasse ?? prev.strasse,
                ort: report.ort ?? prev.ort,
                berichtnummer: report.berichtnummer ?? prev.berichtnummer,
                ersteller: report.ersteller ?? prev.ersteller,
                uhrzeit: report.uhrzeit ?? prev.uhrzeit,
                bewoelkung: report.bewoelkung ?? prev.bewoelkung,
                wetterlage: report.wetterlage ?? prev.wetterlage,
                wind: report.wind ?? prev.wind,
                temperatur: report.temperatur ?? prev.temperatur,
                luftfeuchtigkeit: report.luftfeuchtigkeit ?? prev.luftfeuchtigkeit,
                anwesende: report.anwesende ? (typeof report.anwesende === 'string' ? JSON.parse(report.anwesende) : report.anwesende) : prev.anwesende,
                anmerkungen: report.anmerkungen ?? prev.anmerkungen ?? '',
                // Additional fields from backend
                beauftragte_personen: report.beauftragte_personen ?? prev.beauftragte_personen,
                beauftragungsart: report.beauftragungsart ?? prev.beauftragungsart,
                beauftragungsbeschreibung: report.beauftragungsbeschreibung ?? prev.beauftragungsbeschreibung,
                unterschrift: report.unterschrift ?? prev.unterschrift,
                status: report.status ?? prev.status,
              }));
              setExistingPhotos(report.photos || []);
              
              // Load project data if project_id exists but project-related fields are empty
              if (report.project_id && (!report.projekt || !report.strasse || !report.ort)) {
                try {
                  const project = await projectsAPI.getProject(report.project_id);
                  setFormValues(prev => ({
                    ...prev,
                    projekt: prev.projekt || project.name || '',
                    strasse: prev.strasse || (project.site_address ? project.site_address.split('\n')[0] : ''),
                    ort: prev.ort || (project.site_address ? extractCityFromAddress(project.site_address) : ''),
                  }));
                } catch (projectError) {
                  console.error('Error loading project data:', projectError);
                }
              }
            } catch (error) {
              console.error('Error loading report:', error);
              enqueueSnackbar('Fehler beim Laden des Berichts', { variant: 'error' });
            } finally {
              setLoading(false);
            }
          };
          loadReport();
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        enqueueSnackbar('Fehler beim Laden der Daten', { variant: 'error' });
      }
    };
    fetchData();
  }, [id, user?.email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const formData = new FormData();
      
      // Convert form values to JSON string and append to FormData
      const reportData = {
        ...formValues,
        date: formValues.datum instanceof Date ? formValues.datum.toISOString().split('T')[0] : formValues.datum,
        anwesende: JSON.stringify(formValues.anwesende),
        // Ensure all fields are properly sent to backend
        beauftragte_personen: JSON.stringify(formValues.beauftragte_personen || []),
      };
      console.log('Sending reportData:', reportData); // Debug log
      formData.append('report', JSON.stringify(reportData));

      // Append photos if any
      if (photos.length > 0) {
        photos.forEach(photo => {
          formData.append('photos', photo);
        });
      }

      // Append documents if any
      if (documents.length > 0) {
        documents.forEach(document => {
          formData.append('documents', document);
        });
      }

      if (id) {
        await dailyReportsAPI.updateReport(Number(id), formData);
        enqueueSnackbar('Tagesbericht erfolgreich aktualisiert', { variant: 'success' });
      } else {
        await dailyReportsAPI.createReport(formData);
        enqueueSnackbar('Tagesbericht erfolgreich erstellt', { variant: 'success' });
      }
      navigate('/tagesberichte');
    } catch (error) {
      console.error('Error submitting report:', error);
      enqueueSnackbar('Fehler beim Speichern des Tagesberichts', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  // Wetter zum Berichtsdatum laden (bestehende Werte nur bei Erfolg überschreiben)
  const handleLoadWeather = async () => {
    try {
      const selectedProject = projects.find(p => p.value === formValues.project_id);
      const address = selectedProject?.project?.site_address || formValues.strasse || formValues.ort || 'Berlin';
      const reportDate =
        formValues.datum instanceof Date
          ? formValues.datum.toISOString().split('T')[0]
          : String(formValues.datum || '');

      const weatherData = await dailyReportsAPI.getWeatherForAddress(address, reportDate);

      if (weatherData.success) {
        setFormValues(prev => ({
          ...prev,
          uhrzeit: weatherData.uhrzeit || prev.uhrzeit,
          bewoelkung: weatherData.bewoelkung || prev.bewoelkung,
          wetterlage: weatherData.wetterlage || prev.wetterlage,
          wind: weatherData.wind || prev.wind,
          temperatur: weatherData.temperatur || prev.temperatur,
          luftfeuchtigkeit: weatherData.luftfeuchtigkeit || prev.luftfeuchtigkeit
        }));
        const sourceHint = weatherData.source === 'historical' ? ' (historisch zum Berichtsdatum)' : '';
        enqueueSnackbar(`Wetterdaten erfolgreich geladen${sourceHint}`, { variant: 'success' });
      } else {
        enqueueSnackbar(weatherData.error || 'Fehler beim Laden der Wetterdaten', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error loading weather:', error);
      enqueueSnackbar('Fehler beim Laden der Wetterdaten', { variant: 'error' });
    }
  };

  // Automatisch Wetter laden wenn Projekt ausgewählt wird
  const handleProjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormValues(prev => ({ ...prev, project_id: value }));
    
    // Projekt-Daten laden
    const selectedProject = projects.find(p => p.value === Number(value));
    if (selectedProject) {
      setFormValues(prev => ({
        ...prev,
        projekt: selectedProject.project.name,
        strasse: selectedProject.project.site_address || '',
        ort: selectedProject.project.site_address?.split(',').pop()?.trim() || ''
      }));
      
      // Automatisch Wetter laden wenn Adresse verfügbar
      if (selectedProject.project.site_address) {
        setTimeout(() => handleLoadWeather(), 500); // Kurze Verzögerung
      }
      
      // Berichtnummer wird automatisch über useEffect generiert
    }
  };

  const handleAnwesendeChange = (idx: number, field: keyof AnwesenderEintrag, value: string | number) => {
    const newAnwesende = [...formValues.anwesende];
    newAnwesende[idx] = { ...newAnwesende[idx], [field]: value };
    setFormValues(prev => ({ ...prev, anwesende: newAnwesende }));
  };

  const handleAddAnwesender = () => {
    setFormValues(prev => ({
      ...prev,
      anwesende: [
        ...prev.anwesende,
        { firma: '', personenzahl: '', taetigkeit: '', notiz: '' }
      ]
    }));
  };

  const handleRemoveAnwesender = (idx: number) => {
    setFormValues(prev => ({
      ...prev,
      anwesende: prev.anwesende.filter((_, i) => i !== idx)
    }));
  };

  const handleDeleteDocument = async (documentId: number) => {
    try {
      await dailyReportsAPI.deleteDocument(documentId);
      setExistingDocuments(prev => prev.filter(doc => doc.id !== documentId));
      enqueueSnackbar('Dokument erfolgreich gelöscht', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting document:', error);
      enqueueSnackbar('Fehler beim Löschen des Dokuments', { variant: 'error' });
    }
  };

  const handleConfirmDeletePhoto = async () => {
    if (!photoToDelete || !id) {
      setPhotoToDelete(null);
      return;
    }
    try {
      await dailyReportsAPI.deletePhoto(Number(id), photoToDelete.id);
      setExistingPhotos(prev => prev.filter(p => p.id !== photoToDelete.id));
      setPhotoToDelete(null);
      enqueueSnackbar('Foto erfolgreich gelöscht', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting photo:', error);
      enqueueSnackbar('Fehler beim Löschen des Fotos', { variant: 'error' });
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h5" component="h1" gutterBottom>
        {id ? 'Tagesbericht bearbeiten' : 'Neuer Tagesbericht'}
      </Typography>
      
      {/* Grunddaten-Tabelle */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Grunddaten</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Name</TableCell>
                  <TableCell>
                    <TextField
                      name="name"
                      value={formValues.name}
                      onChange={handleFieldChange}
                      fullWidth
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Projekt</TableCell>
                  <TableCell>
                    <TextField
                      select
                      name="project_id"
                      value={formValues.project_id}
                      onChange={handleProjectChange}
                      fullWidth
                      size="small"
                      required
                    >
                      {projects.map(p => (
                        <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Straße</TableCell>
                  <TableCell>
                    <TextField
                      name="strasse"
                      value={formValues.strasse}
                      onChange={handleFieldChange}
                      fullWidth
                      size="small"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Ort</TableCell>
                  <TableCell>
                    <TextField
                      name="ort"
                      value={formValues.ort}
                      onChange={handleFieldChange}
                      fullWidth
                      size="small"
                      required
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Berichtnummer</TableCell>
                  <TableCell>
                    <TextField
                      name="berichtnummer"
                      value={formValues.berichtnummer}
                      onChange={handleFieldChange}
                      fullWidth
                      size="small"
                      required
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Datum</TableCell>
                  <TableCell>
                    <TextField
                      name="datum"
                      type="date"
                      value={formValues.datum instanceof Date ? formValues.datum.toISOString().split('T')[0] : formValues.datum}
                      onChange={e => handleFieldChange(e)}
                      fullWidth
                      size="small"
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Ersteller</TableCell>
                  <TableCell>
                    <TextField
                      name="ersteller"
                      value={formValues.ersteller}
                      onChange={handleFieldChange}
                      fullWidth
                      size="small"
                      required
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Witterung */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Witterung</Typography>
            <Button
              variant="outlined"
              onClick={handleLoadWeather}
              startIcon={<span>🌤️</span>}
              size="small"
            >
              Wetter laden
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Uhrzeit</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Bewölkung</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Wetterlage</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Wind</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Temperatur</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Luftfeuchtigkeit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <TextField
                      name="uhrzeit"
                      value={formValues.uhrzeit}
                      onChange={handleFieldChange}
                      size="small"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      name="bewoelkung"
                      value={formValues.bewoelkung}
                      onChange={handleFieldChange}
                      size="small"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      name="wetterlage"
                      value={formValues.wetterlage}
                      onChange={handleFieldChange}
                      size="small"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      name="wind"
                      value={formValues.wind}
                      onChange={handleFieldChange}
                      size="small"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      name="temperatur"
                      value={formValues.temperatur}
                      onChange={handleFieldChange}
                      size="small"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      name="luftfeuchtigkeit"
                      value={formValues.luftfeuchtigkeit}
                      onChange={handleFieldChange}
                      size="small"
                      fullWidth
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Anwesende */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Anwesende</Typography>
          
          {/* Mobile View (Card-basiert) */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {formValues.anwesende.map((anwesender, idx) => (
              <Card 
                key={idx} 
                variant="outlined" 
                sx={{ 
                  mb: 2, 
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  backgroundColor: '#fafafa'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Anwesender {idx + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveAnwesender(idx)}
                    sx={{ 
                      backgroundColor: 'rgba(211, 47, 47, 0.08)',
                      '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.15)' }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                
                <TextField
                  select
                  label="Firma"
                  value={anwesender.firma}
                  onChange={e => handleAnwesendeChange(idx, 'firma', e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                  size="medium"
                >
                  <MenuItem value="">Bitte wählen...</MenuItem>
                  <MenuItem value="Rooftop">Rooftop</MenuItem>
                  <MenuItem value="Rooftop AL">Rooftop AL</MenuItem>
                  <MenuItem value="Rooftop RU">Rooftop RU</MenuItem>
                  <MenuItem value="Rooftop SL">Rooftop SL</MenuItem>
                </TextField>
                
                <TextField
                  type="number"
                  label="Personenzahl"
                  value={anwesender.personenzahl || ''}
                  onChange={e => handleAnwesendeChange(idx, 'personenzahl', e.target.value === '' ? '' : Number(e.target.value))}
                  fullWidth
                  sx={{ mb: 2 }}
                  size="medium"
                  placeholder="Anzahl eingeben"
                />
                
                <TextField
                  label="Tätigkeit"
                  value={anwesender.taetigkeit}
                  onChange={e => handleAnwesendeChange(idx, 'taetigkeit', e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                  size="medium"
                  multiline
                  rows={2}
                />
                
                <TextField
                  label="Arbeitszeit (optional)"
                  value={anwesender.notiz}
                  onChange={e => handleAnwesendeChange(idx, 'notiz', e.target.value)}
                  fullWidth
                  size="medium"
                  multiline
                  rows={2}
                />
              </Card>
            ))}
            
            <Button
              variant="outlined"
              onClick={handleAddAnwesender}
              fullWidth
              size="large"
              sx={{ 
                mt: 1,
                py: 1.5,
                fontSize: '1rem',
                borderStyle: 'dashed',
                borderWidth: 2
              }}
            >
              + Anwesenden hinzufügen
            </Button>
          </Box>

          {/* Desktop View (Tabelle) */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Firma</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Personenzahl</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tätigkeit</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Arbeitszeit</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formValues.anwesende.map((anwesender, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <TextField
                          select
                          value={anwesender.firma}
                          onChange={e => handleAnwesendeChange(idx, 'firma', e.target.value)}
                          size="small"
                          fullWidth
                        >
                          <MenuItem value="">Bitte wählen...</MenuItem>
                          <MenuItem value="Rooftop">Rooftop</MenuItem>
                          <MenuItem value="Rooftop AL">Rooftop AL</MenuItem>
                          <MenuItem value="Rooftop RU">Rooftop RU</MenuItem>
                          <MenuItem value="Rooftop SL">Rooftop SL</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={anwesender.personenzahl || ''}
                          onChange={e => handleAnwesendeChange(idx, 'personenzahl', e.target.value === '' ? '' : Number(e.target.value))}
                          size="small"
                          fullWidth
                          placeholder="Anzahl eingeben"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={anwesender.taetigkeit}
                          onChange={e => handleAnwesendeChange(idx, 'taetigkeit', e.target.value)}
                          size="small"
                          fullWidth
                          multiline
                          maxRows={3}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={anwesender.notiz}
                          onChange={e => handleAnwesendeChange(idx, 'notiz', e.target.value)}
                          size="small"
                          fullWidth
                          multiline
                          maxRows={3}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleRemoveAnwesender(idx)}
                        >
                          Entfernen
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Button
                        variant="outlined"
                        onClick={handleAddAnwesender}
                        fullWidth
                      >
                        + Anwesenden hinzufügen
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </CardContent>
      </Card>

      {/* Anmerkungen */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Anmerkungen</Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Zusätzliche Anmerkungen zum Tagesbericht"
            value={formValues.anmerkungen}
            onChange={handleFieldChange}
            name="anmerkungen"
            placeholder="Hier können Sie zusätzliche Anmerkungen, Besonderheiten oder wichtige Hinweise eintragen..."
          />
        </CardContent>
      </Card>

      {/* Fotos */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Bilder</Typography>
          {existingPhotos.length > 0 && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              {existingPhotos.map((photo, index) => (
                <Box key={photo.id} sx={{ position: 'relative' }}>
                  <img
                    src={`${API_BASE_URL.replace('/api', '')}/${photo.file_path}`}
                    alt={`Foto ${index + 1}`}
                    style={{ 
                      width: 120, 
                      height: 120, 
                      objectFit: 'cover',
                      borderRadius: 4,
                      border: '2px solid #ddd',
                      cursor: 'pointer'
                    }}
                    onClick={() => setPreviewPhoto(`${API_BASE_URL.replace('/api', '')}/${photo.file_path}`)}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      '&:hover': {
                        backgroundColor: 'rgba(244, 67, 54, 0.1)',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s'
                    }}
                    onClick={() => setPhotoToDelete(photo)}
                  >
                    <DeleteIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
          
          <Dialog open={!!previewPhoto} onClose={() => setPreviewPhoto(null)} maxWidth="md">
            <DialogTitle>Foto-Vorschau</DialogTitle>
            <DialogContent>
              {previewPhoto && (
                <img src={previewPhoto} alt="Vorschau" style={{ maxWidth: '100%', maxHeight: '70vh' }} />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreviewPhoto(null)}>Schließen</Button>
            </DialogActions>
          </Dialog>
          
          <Dialog open={!!photoToDelete} onClose={() => setPhotoToDelete(null)}>
            <DialogTitle>Foto löschen?</DialogTitle>
            <DialogContent>
              <Typography>Möchtest du dieses Foto wirklich löschen?</Typography>
              {photoToDelete && (
                <img src={`${API_BASE_URL.replace('/api', '')}/${photoToDelete.file_path}`} alt="Zu löschendes Foto" style={{ width: 200, marginTop: 16 }} />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPhotoToDelete(null)}>Abbrechen</Button>
              <Button color="error" onClick={handleConfirmDeletePhoto}>Löschen</Button>
            </DialogActions>
          </Dialog>
          
          {/* iOS-kompatibler Foto-Upload ohne accept-Attribute */}
          <Button
            variant="outlined"
            startIcon={<PhotoCameraIcon />}
            sx={{ mb: 1 }}
            onClick={() => {
              // Erstelle einen versteckten File-Input für iOS-Kompatibilität
              const input = document.createElement('input');
              input.type = 'file';
              input.multiple = true;
              // KEINE accept-Attribute - das verursacht iOS-Probleme
              input.style.display = 'none';
              input.onchange = (e: Event) => {
                const target = e.target as HTMLInputElement;
                const fileList = target.files ? Array.from(target.files) : [];
                setPhotos(fileList);
              };
              document.body.appendChild(input);
              input.click();
              document.body.removeChild(input);
            }}
          >
            Fotos auswählen
          </Button>
          <Typography variant="body2" color="textSecondary">
            Fotos hier ablegen oder klicken zum Auswählen (max. 10 Fotos)
          </Typography>

          {/* Anzeige der neu ausgewählten Fotos */}
          {photos.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Neu ausgewählte Fotos ({photos.length}):
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {photos.map((photo, index) => (
                  <Box key={index} sx={{ position: 'relative' }}>
                    <img
                      src={URL.createObjectURL(photo)}
                      alt={`Neues Foto ${index + 1}`}
                      style={{ 
                        width: 120, 
                        height: 120, 
                        objectFit: 'cover', 
                        borderRadius: 4,
                        border: '2px solid #4caf50'
                      }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.1)',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s'
                      }}
                      onClick={() => {
                        const newPhotos = photos.filter((_, i) => i !== index);
                        setPhotos(newPhotos);
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dokumente */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Dateien</Typography>
          {existingDocuments.length > 0 && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              {existingDocuments.map((doc, index) => (
                <Box 
                  key={doc.id} 
                  sx={{ 
                    position: 'relative', 
                    display: 'flex', 
                    alignItems: 'center', 
                    border: '1px solid #eee', 
                    borderRadius: 1, 
                    p: 1.5,
                    minWidth: 200,
                    backgroundColor: '#f9f9f9'
                  }}
                >
                  <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {doc.original_filename}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {(doc.file_size / 1024).toFixed(1)} KB
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteDocument(doc.id)}
                    sx={{ ml: 1 }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
          <Button
            variant="outlined"
            startIcon={<AttachFileIcon />}
            sx={{ mb: 1 }}
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.multiple = true;
              input.style.display = 'none';
              input.onchange = (e: Event) => {
                const target = e.target as HTMLInputElement;
                const fileList = target.files ? Array.from(target.files) : [];
                setDocuments(prev => [...prev, ...fileList]);
              };
              document.body.appendChild(input);
              input.click();
              document.body.removeChild(input);
            }}
          >
            Dateien auswählen
          </Button>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Hier können Sie weitere Dateien (z.B. PDF, Word, Excel) anhängen. Diese werden nicht im PDF-Bericht angezeigt.
          </Typography>
          {documents.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Neu ausgewählte Dateien ({documents.length}):
              </Typography>
              <List dense>
                {documents.map((file, index) => (
                  <ListItem 
                    key={index} 
                    sx={{ 
                      px: 0,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor: '#fafafa'
                    }}
                  >
                    <ListItemIcon>
                      <InsertDriveFileIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary={file.name} 
                      secondary={`${(file.size / 1024).toFixed(2)} KB`} 
                    />
                    <IconButton 
                      edge="end" 
                      aria-label="delete" 
                      onClick={() => setDocuments(prev => prev.filter((_, i) => i !== index))}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            select
            name="status"
            label="Status"
            value={formValues.status}
            onChange={handleFieldChange}
            fullWidth
            required
          >
            <MenuItem value="entwurf">Entwurf</MenuItem>
            <MenuItem value="eingereicht">Eingereicht</MenuItem>
            <MenuItem value="genehmigt">Genehmigt</MenuItem>
          </TextField>
        </CardContent>
      </Card>

      {/* Speichern-Button */}
      <Box display="flex" justifyContent="flex-end" mb={4}>
        <Button type="submit" variant="contained" color="primary" disabled={loading}>
          {loading ? 'Speichern...' : 'Speichern'}
        </Button>
      </Box>
    </Box>
  );
}; 